import React, { useEffect, useState } from "react";
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { BUTTON_VARIANT, BUTTON_COLOR } from "../../../../types/coreButtonTypes";
import ModalBody from "../../../core-components/modal-box/modal-body/ModalBody";
import ModalFooter from "../../../core-components/modal-box/modal-footer/ModalFooter";
import ModalHeader from "../../../core-components/modal-box/modal-header/ModalHeader";
import ModalBoxCore from "../../../core-components/modal-box/ModalBoxCore";
import CoreButtons from "../../../core-components/button/CoreButtons";
import CheckboxLabel from "../../../core-components/checkbox-label/CheckboxLabel";
import CarretDown from "../../../../assets/svg-fc/CarretDown";
import { IQuoteFormSection } from "../../../../types/quoteFormTypes";
import { SectionTypeEnum } from "../../../../constants/enums";
import "./confirmCopyModal.scss";

interface IConfirmCopyModalProps {
	open: boolean;
	sections: IQuoteFormSection[];
	onClose: () => void;
	onConfirm: (isFinancialDataChecked: boolean, checkedSections: string[]) => void;
}

const ConfirmCopyModal: React.FC<IConfirmCopyModalProps> = ({ open, sections, onClose, onConfirm }) => {
	const [isFinancialDataChecked, setIsFinancialDataChecked] = useState(true);
	const [isMenuAndServicesChecked, setIsMenuAndServicesChecked] = useState(true);
	const [checkedSections, setCheckedSections] = useState<string[]>([]);

	useEffect(() => {
		setCheckedSections(sections?.map((section) => section.id) ?? []);

		return () => {
			setIsFinancialDataChecked(true);
			setIsMenuAndServicesChecked(true);
			setCheckedSections([]);
		};
	}, [sections]);

	const handleConfirm = () => {
		onConfirm(isFinancialDataChecked, checkedSections);
		onClose();
	};

	return (
		<ModalBoxCore className="confirmCopyModal" open={open} onClose={onClose}>
			<ModalHeader modalHeaderText="Confirm copying from previous quote" closeModal={onClose} />
			<ModalBody className="confirmCopyModalBody">
				<Typography className="confirmCopyModalDescription">
					The information you entered in the form will be replaced by the details from the selected quote.
					Select the quote sections you want to copy
				</Typography>
				<Box className="confirmCopyModalSections">
					<CheckboxLabel
						label="Financial section"
						checked={isFinancialDataChecked}
						onChange={(e) => setIsFinancialDataChecked(e.target.checked)}
					/>
					<Accordion key="menu-services" className="confirmCopyModalAccordion" defaultExpanded>
						<AccordionSummary expandIcon={<CarretDown />} className="confirmCopyModalAccordionSummary">
							<CheckboxLabel
								label="Menu & services section"
								checked={isMenuAndServicesChecked}
								onChange={(e) => {
									setIsMenuAndServicesChecked(e.target.checked);
									setCheckedSections(e.target.checked ? sections?.map((section) => section.id) : []);
								}}
							/>
						</AccordionSummary>
						<AccordionDetails className="confirmCopyModalAccordionDetails">
							<Box className="confirmCopyModalSubSections">
								{sections?.map((subSection) => (
									<CheckboxLabel
										key={subSection.id}
										label={
											subSection?.sectionType === SectionTypeEnum.SERVICE
												? "Services"
												: subSection.sectionTitle
										}
										checked={checkedSections?.includes(subSection?.id)}
										onChange={() => {
											const newCheckedSections = checkedSections?.includes(subSection?.id)
												? checkedSections?.filter((id) => id !== subSection?.id)
												: [...checkedSections, subSection?.id];
											setCheckedSections(newCheckedSections);
											setIsMenuAndServicesChecked(newCheckedSections?.length > 0);
										}}
									/>
								))}
							</Box>
						</AccordionDetails>
					</Accordion>
				</Box>
			</ModalBody>
			<ModalFooter
				buttonText="Cancel"
				coreBtnPill
				fullWidth
				variant={BUTTON_VARIANT.OUTLINED}
				color={BUTTON_COLOR.SECONDARY}
				onclick={onClose}
				modalFooterClassName="confirmCopyModalFooterContainer"
			>
				<CoreButtons
					buttonText="Confirm"
					coreBtnPill
					fullWidth
					variant={BUTTON_VARIANT.CONTAINED}
					color={BUTTON_COLOR.PRIMARY}
					disabled={!isFinancialDataChecked && !isMenuAndServicesChecked}
					onclick={handleConfirm}
					sx={{ textTransform: "none" }}
				/>
			</ModalFooter>
		</ModalBoxCore>
	);
};

export default ConfirmCopyModal;
